import { useState, useEffect } from "react";
import { Typography, Grid, Box } from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import SingleSelect from "../../components/selections/singleSelect";
import CustomButton from "../../components/buttons/customButton";
import { useDispatch, useSelector } from "react-redux";
import MultiSelectWithAll from "../../components/selections/multiSelectWithAll";
import SnackbarMessage from "../snackBar/snackBar";
import {
    getTagsThunk,
    updateContactThunk,
    getContactThunk,
    getIndividualUserThunk,
} from "../../features/contact/contactThunk";

export default function ProfileTab({ userData }) {    
    const dispatch = useDispatch();

    const [state, setState] = useState({
        open: false,
        message: "",
        severity: "error",
        errors: {},
        profileTags: [],
        organization: [],
        branchDepartmentValue: [],
        name: "",
        email: "",
    });

    const [changedFields, setChangedFields] = useState(new Set());
    const [disabled, setDisabled] = useState(true);

    const { getOrganizations, getTags } = useSelector((state) => state.contacts);
    const { branchDepartments } = useSelector((state) => state.general);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // ✅ Initialize name and email from userData
    useEffect(() => {
        if (userData) {
            setState((prev) => ({
                ...prev,
                name: userData.name || "",
                email: userData.email || "",
            }));
        }
    }, [userData]);

    const handleChange = (eOrName, maybeValue) => {
        if (typeof eOrName === "string") {
            const name = eOrName;
            const value = maybeValue;
            setState((prev) => ({
                ...prev,
                [name]: value,
                errors: { ...prev.errors, [name]: "" },
            }));
        } else {
            const { name, value } = eOrName.target;
            if (value.startsWith(" ")) return;
            setState((prev) => ({
                ...prev,
                [name]: value,
                errors: { ...prev.errors, [name]: "" },
            }));
            setChangedFields((prev) => new Set(prev).add(name));
        }
    };

    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
        setChangedFields((prev) => new Set(prev).add(name));
    };

    const handleMultiSelectChange = (name, selectedValues) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValues,
            errors: { ...prev.errors, [name]: "" },
        }));
        setChangedFields((prev) => new Set(prev).add(name));
    };

    const fields = [
        { id: 1, type: "text", name: "name", label: "Name" },
        { id: 2, type: "text", name: "email", label: "Email" },
        {
            id: 3,
            type: "multi-select",
            name: "branchDepartmentValue",
            label: "Branch Department",
            options: branchDepartments.map((item) => ({
                value: item.id,
                label: item.branch_name + " - " + item.department_name,
            })),
        },
        {
            id: 4,
            type: "multi-select",
            name: "profileTags",
            label: "Tags",
            options: getTags.map((item) => ({
                value: item.id,
                label: item.tag_name,
            })),
        },
        {
            id: 5,
            type: "multi-select",
            name: "organization",
            label: "Organizations",
            options: getOrganizations.map((item) => ({
                value: item.id,
                label: item.organization_name,
            })),
        },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!changedFields || changedFields.size === 0) {
            setSnackbar({
                open: true,
                message: "No changes to update",
                severity: "info",
            });
            return;
        }

        const payload = {};

        if (changedFields.has("name")) payload.name = state.name;
        if (changedFields.has("email")) payload.email = state.email;
        if (changedFields.has("branchDepartmentValue"))
            payload.branch_department_id = state.branchDepartmentValue
                .map((item) => item.value)
                .toString();
        if (changedFields.has("organization"))
            payload.organization_id = state.organization.map((item) => item.value).toString();
        if (changedFields.has("profileTags"))
            payload.tag_id = state.profileTags.map((item) => item.value).toString();

        const payload1 = {
            ...payload,
            wa_contact_id: userData?.id,
        };

        try {
            const result = await dispatch(updateContactThunk({ credentials: payload1 }));

            
            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });
                dispatch(getTagsThunk({ type: "tags" }));
                dispatch(getContactThunk({ type: "getContact" }));
                dispatch(getIndividualUserThunk({ id: userData?.id }));
                setDisabled(true)

                setState((prev) => ({
                    ...prev,
                    isCreateTag: false,
                    searchTags: "",
                }));
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    const handleEdit = () => setDisabled(false);

    // ✅ Pre-select Tags
    useEffect(() => {
        if (userData?.tag_id && getTags?.length > 0) {
            const tagIds = userData.tag_id.split(",").map((id) => parseInt(id.trim(), 10));
            const selectedTags = getTags
                .filter((tag) => tagIds.includes(tag.id))
                .map((tag) => ({
                    value: tag.id,
                    label: tag.tag_name,
                }));
            setState((prev) => ({ ...prev, profileTags: selectedTags }));
        } else {
            setState((prev) => ({ ...prev, profileTags: [] }));
        }
    }, [userData, getTags]);

    // ✅ Pre-select Branch Departments
    useEffect(() => {
        if (userData?.branch_department_id && branchDepartments?.length > 0) {
            const branchDeptIds = userData.branch_department_id
                .split(",")
                .map((id) => parseInt(id.trim(), 10));
            const selectedBranchDepts = branchDepartments
                .filter((bd) => branchDeptIds.includes(bd.id))
                .map((bd) => ({
                    value: bd.id,
                    label: bd.branch_name + " - " + bd.department_name,
                }));
            setState((prev) => ({ ...prev, branchDepartmentValue: selectedBranchDepts }));
        } else {
            setState((prev) => ({ ...prev, branchDepartmentValue: [] }));
        }
    }, [userData, branchDepartments]);

    // ✅ Pre-select Organizations
    useEffect(() => {
        if (userData?.organization_ids && getOrganizations?.length > 0) {
            const orgIds = userData.organization_ids
                .split(",")
                .map((id) => parseInt(id.trim(), 10));
            const selectedOrgs = getOrganizations
                .filter((org) => orgIds.includes(org.id))
                .map((org) => ({
                    value: org.id,
                    label: org.organization_name,
                }));
            setState((prev) => ({ ...prev, organization: selectedOrgs }));
        } else {
            setState((prev) => ({ ...prev, organization: [] }));
        }
    }, [userData, getOrganizations]);

    return (
        <>
            <Grid>
                {disabled && (
                    <Box mb={2} sx={{ display: "flex", justifyContent: "end" }}>
                        <Button onClick={handleEdit} variant="contained">
                            Edit
                        </Button>
                    </Box>
                )}


                <Box sx={{ width: "100%" }}>
                    <Grid container spacing={2}>
                        {fields.map((field) => (
                            <Grid key={field.id} item size={{ xs: 12, md: 6 }} sx={{ mt: 1 }}>
                                {field.type === "text" ? (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label={field.label}
                                        name={field.name}
                                        value={state[field.name] ?? ""}
                                        onChange={handleChange}
                                        disabled={disabled}
                                        inputProps={{ maxLength: 100 }}
                                        error={Boolean(state.errors?.[field?.name])}
                                    />
                                ) : field.type === "select" ? (
                                    <SingleSelect
                                        name={field.name}
                                        label={field.label}
                                        options={field.options}
                                        value={state[field.name]}
                                        onChange={handleSingleSelectChange}
                                        error={!!state.errors[field.name]}
                                        formHelperText={false}
                                        disabled={disabled}
                                    />
                                ) : field.type === "multi-select" ? (
                                    <MultiSelectWithAll
                                        name={field.name}
                                        label={field.label}
                                        options={field.options}
                                        value={state[field.name] || []}
                                        onChange={handleMultiSelectChange}
                                        formHelperText={false}
                                        error={!!state.errors[field.name]}
                                        disabled={disabled}
                                    />
                                ) : null}
                            </Grid>
                        ))}
                    </Grid>

                    {!disabled && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                            <CustomButton onClick={handleSubmit} label="Update" loadingLabel="Updating..." />
                        </Box>
                    )}
                </Box>
            </Grid>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </>
    );
}
